import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowPathIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  CubeIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  ShoppingBagIcon,
  TruckIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import {
  fetchAdminDashboardSummary,
  AdminStats,
  RecentOrder,
  LowStockItem,
} from "../../services/adminDashboardService";

const parseJson = <T,>(value: string | null): T | null => {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

const getTenantId = () => {
  const tenant = parseJson<{ id?: string }>(localStorage.getItem("currentTenant"));
  const userData = parseJson<{ tenant_id?: string }>(localStorage.getItem("user_data"));
  const user = parseJson<{ tenant_id?: string }>(localStorage.getItem("user"));
  return tenant?.id || userData?.tenant_id || user?.tenant_id || "";
};

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 0,
  }).format(Number(amount || 0));

const formatDate = (value?: string | null) => {
  if (!value) return "-";
  const dt = new Date(value);
  return Number.isNaN(dt.getTime()) ? value : dt.toLocaleString();
};

const statusBadgeClass = (status: string) => {
  const normalized = (status || "").toLowerCase();
  if (normalized === "pending") return "bg-amber-100 text-amber-700";
  if (normalized === "processing") return "bg-blue-100 text-blue-700";
  if (normalized === "shipped") return "bg-purple-100 text-purple-700";
  if (normalized === "delivered") return "bg-green-100 text-green-700";
  if (normalized === "cancelled") return "bg-red-100 text-red-700";
  return "bg-gray-100 text-gray-700";
};

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<AdminStats>({
    totalProducts: 0,
    totalCustomers: 0,
    pendingOrders: 0,
    monthlyRevenue: 0,
    lowStockItems: 0,
    completedDeliveries: 0,
  });
  const [recentOrders, setRecentOrders] = useState<RecentOrder[]>([]);
  const [lowStockItems, setLowStockItems] = useState<LowStockItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const tenantId = useMemo(() => getTenantId(), []);

  const loadDashboard = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      setError(null);
      const data = await fetchAdminDashboardSummary();
      setStats(data.stats);
      setRecentOrders(data.recentOrders || []);
      setLowStockItems(data.lowStockItems || []);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to load dashboard data");
    } finally {
      if (isRefresh) setRefreshing(false);
      else setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const fulfillmentRate = useMemo(() => {
    const totalTracked = Number(stats.pendingOrders || 0) + Number(stats.completedDeliveries || 0);
    if (!totalTracked) return 0;
    return Math.round((Number(stats.completedDeliveries || 0) / totalTracked) * 100);
  }, [stats.completedDeliveries, stats.pendingOrders]);

  const dashboardCards = [
    {
      label: "Products",
      value: stats.totalProducts.toLocaleString(),
      sublabel: "catalog items",
      icon: ShoppingBagIcon,
      accent: "text-blue-700 bg-blue-100",
      href: "/admin/products",
    },
    {
      label: "Orders Pending",
      value: String(stats.pendingOrders || 0),
      sublabel: "awaiting action",
      icon: ClipboardDocumentListIcon,
      accent: "text-amber-700 bg-amber-100",
      href: "/admin/orders",
    },
    {
      label: "Customers",
      value: stats.totalCustomers.toLocaleString(),
      sublabel: "tenant customer accounts",
      icon: UserGroupIcon,
      accent: "text-emerald-700 bg-emerald-100",
      href: "/admin/customers",
    },
    {
      label: "Monthly Revenue",
      value: formatCurrency(stats.monthlyRevenue || 0),
      sublabel: "current summary window",
      icon: CurrencyDollarIcon,
      accent: "text-indigo-700 bg-indigo-100",
      href: "/admin/analytics",
    },
    {
      label: "Low Stock Alerts",
      value: String(stats.lowStockItems || 0),
      sublabel: "replenishment needed",
      icon: ExclamationTriangleIcon,
      accent: "text-red-700 bg-red-100",
      href: "/admin/products",
    },
    {
      label: "Completed Deliveries",
      value: String(stats.completedDeliveries || 0),
      sublabel: `${fulfillmentRate}% fulfillment rate`,
      icon: TruckIcon,
      accent: "text-green-700 bg-green-100",
      href: "/admin/orders",
    },
  ];

  const opsAlerts = useMemo(() => {
    const alerts: Array<{ level: "warning" | "info" | "good"; title: string; detail: string }> = [];
    if ((stats.pendingOrders || 0) > 10) {
      alerts.push({
        level: "warning",
        title: "Pending order backlog",
        detail: `${stats.pendingOrders} orders are pending. Review fulfillment queue in Orders.`,
      });
    }
    if ((stats.lowStockItems || 0) > 0) {
      alerts.push({
        level: "warning",
        title: "Low stock attention needed",
        detail: `${stats.lowStockItems} products are below threshold. Replenish or update availability.`,
      });
    }
    if ((stats.completedDeliveries || 0) > 0 && fulfillmentRate >= 70) {
      alerts.push({
        level: "good",
        title: "Fulfillment flow healthy",
        detail: `${fulfillmentRate}% completion across tracked deliveries/pending orders.`,
      });
    }
    if (alerts.length === 0) {
      alerts.push({
        level: "info",
        title: "No urgent alerts",
        detail: "Dashboard did not detect immediate product or order risks from summary data.",
      });
    }
    return alerts.slice(0, 3);
  }, [stats, fulfillmentRate]);

  return (
    <div className="max-w-7xl mx-auto p-6 md:p-8 space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-sm text-gray-600 mt-1">
            Tenant command center for catalog, orders, customers, and operational health.
            {tenantId ? <span className="ml-2 font-mono text-xs">Tenant: {tenantId}</span> : null}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => loadDashboard(true)}
            disabled={refreshing}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Refreshing..." : "Refresh"}
          </button>
          <Link to="/admin/products" className="px-4 py-2 rounded-lg bg-green-700 text-white font-semibold hover:bg-green-800">
            Manage Products
          </Link>
          <Link to="/admin/orders" className="px-4 py-2 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50">
            Manage Orders
          </Link>
        </div>
      </div>

      {error && (
        <div className="bg-white border border-red-200 rounded-xl p-5">
          <div className="text-red-700 font-semibold">{error}</div>
          {error.toLowerCase().includes("authentication") && (
            <div className="text-sm text-gray-600 mt-2">
              Re-login to refresh this app session (`auth_token`) for your tenant. The dashboard UI is loaded, but protected API requests are returning `401`.
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {dashboardCards.map((card) => (
          <Link
            key={card.label}
            to={card.href}
            className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-sm hover:border-gray-300 transition"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-500">{card.label}</div>
                <div className="text-2xl font-bold text-gray-900 mt-1">{loading ? "…" : card.value}</div>
                <div className="text-xs text-gray-500 mt-1">{card.sublabel}</div>
              </div>
              <div className={`p-2 rounded-lg ${card.accent}`}>
                <card.icon className="h-5 w-5" />
              </div>
            </div>
          </Link>
        ))}
      </div>

      <div className="grid lg:grid-cols-[1.6fr_1fr] gap-6">
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Recent Orders</h2>
                <p className="text-sm text-gray-600">Latest order activity needing monitoring</p>
              </div>
              <Link to="/admin/orders" className="text-sm font-medium text-green-700 hover:text-green-800">
                Open Orders
              </Link>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Customer</th>
                    <th className="px-4 py-3 text-left font-semibold">Items</th>
                    <th className="px-4 py-3 text-left font-semibold">Status</th>
                    <th className="px-4 py-3 text-left font-semibold">Amount</th>
                    <th className="px-4 py-3 text-left font-semibold">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={5} className="px-4 py-6 text-gray-500">
                        Loading recent orders...
                      </td>
                    </tr>
                  )}
                  {!loading && recentOrders.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-6 text-gray-500">
                        No recent orders in the dashboard summary.
                      </td>
                    </tr>
                  )}
                  {!loading &&
                    recentOrders.map((order) => (
                      <tr key={order.id} className="border-t border-gray-100">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{order.customerName || "Unknown customer"}</div>
                          <div className="text-xs text-gray-400 font-mono mt-1">{order.id}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-700">{order.items || "-"}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${statusBadgeClass(order.status)}`}>
                            {order.status || "unknown"}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-semibold text-gray-900">{formatCurrency(order.amount || 0)}</td>
                        <td className="px-4 py-3 text-gray-600">{formatDate(order.date)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Operations Health</h2>
                <p className="text-sm text-gray-600">Actionable signals from dashboard summary data</p>
              </div>
              <ChartBarIcon className="h-5 w-5 text-gray-400" />
            </div>
            <div className="space-y-3">
              {opsAlerts.map((alert, idx) => (
                <div
                  key={`${alert.title}-${idx}`}
                  className={`rounded-lg border p-3 ${
                    alert.level === "warning"
                      ? "border-amber-200 bg-amber-50"
                      : alert.level === "good"
                      ? "border-green-200 bg-green-50"
                      : "border-blue-200 bg-blue-50"
                  }`}
                >
                  <div className="font-semibold text-gray-900 text-sm">{alert.title}</div>
                  <div className="text-xs text-gray-700 mt-1">{alert.detail}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
            <div className="grid gap-3">
              <Link to="/admin/products" className="flex items-center gap-3 rounded-lg border border-gray-200 px-3 py-3 hover:bg-gray-50">
                <ShoppingBagIcon className="h-5 w-5 text-green-700" />
                <span className="text-sm font-medium text-gray-900">Catalog & Stock Control</span>
              </Link>
              <Link to="/admin/orders" className="flex items-center gap-3 rounded-lg border border-gray-200 px-3 py-3 hover:bg-gray-50">
                <ClipboardDocumentListIcon className="h-5 w-5 text-blue-700" />
                <span className="text-sm font-medium text-gray-900">Fulfillment Queue</span>
              </Link>
              <Link to="/admin/users" className="flex items-center gap-3 rounded-lg border border-gray-200 px-3 py-3 hover:bg-gray-50">
                <UserGroupIcon className="h-5 w-5 text-indigo-700" />
                <span className="text-sm font-medium text-gray-900">User Access Management</span>
              </Link>
              <Link to="/admin/customers" className="flex items-center gap-3 rounded-lg border border-gray-200 px-3 py-3 hover:bg-gray-50">
                <CubeIcon className="h-5 w-5 text-emerald-700" />
                <span className="text-sm font-medium text-gray-900">Customer CRM</span>
              </Link>
              <Link to="/admin/analytics" className="flex items-center gap-3 rounded-lg border border-gray-200 px-3 py-3 hover:bg-gray-50">
                <ChartBarIcon className="h-5 w-5 text-purple-700" />
                <span className="text-sm font-medium text-gray-900">Analytics & AADE Insights</span>
              </Link>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Low Stock Watchlist</h2>
                <p className="text-sm text-gray-600">Products nearing stock risk</p>
              </div>
              <Link to="/admin/products" className="text-sm font-medium text-green-700 hover:text-green-800">
                Open Products
              </Link>
            </div>
            <div className="divide-y divide-gray-100">
              {loading && <div className="p-4 text-sm text-gray-500">Loading stock alerts...</div>}
              {!loading && lowStockItems.length === 0 && (
                <div className="p-4 text-sm text-gray-500">No low stock items reported.</div>
              )}
              {!loading &&
                lowStockItems.map((item) => (
                  <div key={item.id} className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-medium text-gray-900">{item.name}</div>
                        <div className="text-xs text-gray-500 mt-1 capitalize">{item.category || "uncategorized"}</div>
                        <div className="text-xs text-gray-400 mt-1 font-mono">{item.id}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold text-red-700">{item.currentStock}</div>
                        <div className="text-xs text-gray-500">Min {item.minStock}</div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-100 p-5">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Admin Snapshot</h2>
            <div className="text-sm text-gray-700 space-y-2">
              <div className="flex items-center justify-between">
                <span>Fulfillment rate</span>
                <span className="font-semibold text-green-700">{loading ? "…" : `${fulfillmentRate}%`}</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Catalog risk items</span>
                <span className="font-semibold text-amber-700">{loading ? "…" : stats.lowStockItems}</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Revenue visibility</span>
                <span className="font-semibold text-indigo-700">{loading ? "…" : formatCurrency(stats.monthlyRevenue)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
